import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "../i18n";
import { FadeIn, MaskLine, Micro, useHashNav } from "./Reveal";

export default function AboutTeaser() {
  const go = useHashNav();
  const { t } = useLang();
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" data-testid="about-section" className="scroll-mt-24 px-5 md:px-12 py-24 md:py-36 border-t border-white/5">
      <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-center">
        <div className="md:col-span-5">
          <FadeIn>
            <Micro className="text-gold/80">{t("about.chapter")}</Micro>
          </FadeIn>
          <h2 className="font-display uppercase leading-[1.02] text-[clamp(1.7rem,3.8vw,3.2rem)] mt-6">
            <MaskLine delay={0.05}>{t("about.h1a")}</MaskLine>
            <MaskLine delay={0.15}>
              {t("about.h2pre")} <span className="text-gold">{t("about.h2gold")}</span> {t("about.h2post")}
            </MaskLine>
          </h2>
          <FadeIn delay={0.25}>
            <p className="mt-7 text-sm md:text-base text-cream/55 leading-relaxed max-w-md">
              {t("about.p")}
            </p>
          </FadeIn>
          <FadeIn delay={0.35}>
            <button
              data-testid="about-know-more"
              data-cursor="link"
              onClick={() => go("#why")}
              className="group mt-9 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-gold hover:text-goldbright transition-colors border-b border-gold/40 pb-1"
            >
              {t("about.knowMore")} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </FadeIn>
        </div>

        <div className="md:col-span-7">
          <FadeIn delay={0.15}>
            <div ref={imgRef} className="relative overflow-hidden border border-white/10 aspect-[16/10]" data-cursor="view">
              <motion.img
                src="/img/g1.jpg"
                alt={t("about.imgAlt")}
                loading="lazy"
                className="w-full h-[118%] object-cover"
                style={{ y }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />
              <span className="absolute bottom-0 left-0 h-[2px] w-full bg-gold/70" />
              <div className="absolute top-4 right-4">
                <Micro className="text-cream/70">{t("about.est")}</Micro>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}
